<template>
  <div class="admin-categories-container fade-in">
    <!-- ── Page Header ─────────────────────────────────────── -->
    <div class="page-header mb-3">
      <div class="page-header-left">
        <h2 class="page-title">{{ pageTitle }}</h2>
        <p class="page-subtitle">{{ pageSubtitle }}</p>
      </div>
    </div>

    <!-- ── Main View Switcher (Pending vs History) ──────────── -->
    <div class="approval-main-tabs mb-4">
      <button
        class="approval-main-tab"
        :class="{ 'active': mainView === 'pending' }"
        @click="switchMainView('pending')"
      >
        <icon icon="fa-solid fa-clock-rotate-left" class="tab-icon" />
        <span>{{ isAdmin ? 'Yêu cầu chờ xét duyệt' : 'Yêu cầu chờ duyệt của tôi' }}</span>
      </button>
      <button
        class="approval-main-tab"
        :class="{ 'active': mainView === 'history' }"
        @click="switchMainView('history')"
      >
        <icon icon="fa-solid fa-list-check" class="tab-icon" />
        <span>{{ isAdmin ? 'Lịch sử đã xét duyệt' : 'Lịch sử đề xuất của tôi' }}</span>
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════════════ -->
    <!-- VIEW 1: PENDING APPROVALS                               -->
    <!-- ═══════════════════════════════════════════════════════ -->
    <div v-if="mainView === 'pending'" class="dashboard-panel">
      <div class="panel-header">
        <div class="panel-title-group">
          <h3 class="panel-title">{{ isAdmin ? 'Danh sách chờ duyệt' : 'Danh sách đã gửi yêu cầu' }}</h3>
          <span class="panel-subtitle">Tổng số: {{ items.length }} yêu cầu</span>
        </div>
        <div class="panel-actions-wrapper">
          <div class="segmented-control">
            <button class="segment" :class="{ 'active': activeTab === 'book' }" @click="activeTab = 'book'">
              <span>Sách</span>
              <span v-if="pendingCounts.book > 0" class="badge-segment-count">{{ pendingCounts.book }}</span>
            </button>
            <button class="segment" :class="{ 'active': activeTab === 'author' }" @click="activeTab = 'author'">
              <span>Tác giả</span>
              <span v-if="pendingCounts.author > 0" class="badge-segment-count">{{ pendingCounts.author }}</span>
            </button>
            <button class="segment" :class="{ 'active': activeTab === 'translator' }" @click="activeTab = 'translator'">
              <span>Dịch giả</span>
              <span v-if="pendingCounts.translator > 0" class="badge-segment-count">{{ pendingCounts.translator }}</span>
            </button>
            <button class="segment" :class="{ 'active': activeTab === 'category' }" @click="activeTab = 'category'">
              <span>Thể loại</span>
              <span v-if="pendingCounts.category > 0" class="badge-segment-count">{{ pendingCounts.category }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="table-responsive-wrapper">
        <table class="custom-admin-table">
          <thead>
            <tr>
              <th class="col-stt text-center">STT</th>
              <th>Tên {{ entityLabel }}</th>
              <th class="text-center" style="width: 130px;">Loại yêu cầu</th>
              <th>Lý do yêu cầu</th>
              <th class="text-center" style="width: 170px;">Thời gian gửi</th>
              <th class="text-center col-actions">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            <!-- Loading -->
            <template v-if="isLoading">
              <tr v-for="n in 3" :key="'skeleton-' + n" class="skeleton-row">
                <td class="text-center"><span class="skeleton-bar skeleton-text-stt"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td class="text-center">
                  <div class="skeleton-actions">
                    <span class="skeleton-btn"></span>
                    <span class="skeleton-btn"></span>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Data Rows -->
            <template v-else>
              <tr v-for="(item, index) in items" :key="item.id" class="table-row-hover">
                <td class="col-stt text-center text-muted fw-semibold">{{ index + 1 }}</td>
                <td>
                  <div class="item-name-cell">
                    <div v-if="activeTab === 'book'" class="item-cover-wrapper">
                      <img
                        v-if="item.cover"
                        :src="item.cover.startsWith('data:') || item.cover.startsWith('http') ? item.cover : 'data:image/png;base64,' + item.cover"
                        class="item-thumb-mini"
                        alt="Bìa sách"
                        @error="handleImageError($event)"
                      />
                      <div class="item-thumb-mini item-thumb-placeholder" :style="{ display: item.cover ? 'none' : 'flex' }">
                        <icon icon="fa-solid fa-book" />
                      </div>
                    </div>
                    <a v-if="activeTab === 'book'" href="#" @click.prevent="openViewModal(item)" class="row-name row-name-link" title="Click để xem chi tiết sách">
                      {{ item.name }}
                    </a>
                    <span v-else class="row-name">{{ item.name }}</span>
                  </div>
                </td>
                <td class="text-center">
                  <span class="badge-tag" :class="isRestoreItem(item) ? 'badge-action-restore' : 'badge-action-delete'">
                    {{ isRestoreItem(item) ? 'Khôi phục' : 'Xóa bỏ' }}
                  </span>
                </td>
                <td>
                  <span v-if="item.reason" class="text-muted-dark fs-9-5">{{ getCleanReason(item) }}</span>
                  <span v-else class="text-muted fs-9-5 font-italic">Không có lý do</span>
                </td>
                <td class="text-center">
                  <span class="text-muted fs-8-5">{{ formatVersionTime(item.requestTime) }}</span>
                </td>
                <td class="text-center">
                  <div class="action-btn-group">
                    <button @click="openHistoryModal(item)" class="btn-action-icon btn-action-history" data-tooltip="Xem lịch sử">
                      <icon icon="fa-solid fa-clock-rotate-left" />
                    </button>
                    <button v-if="isAdmin" @click="confirmApprove(item)" class="btn-action-icon btn-action-edit" :data-tooltip="isRestoreItem(item) ? 'Phê duyệt khôi phục' : 'Phê duyệt xóa'">
                      <icon :icon="isRestoreItem(item) ? 'fa-solid fa-trash-can-arrow-up' : 'fa-solid fa-check'" />
                    </button>
                    <button v-if="isAdmin" @click="confirmReject(item)" class="btn-action-icon btn-action-delete" :data-tooltip="isRestoreItem(item) ? 'Từ chối khôi phục' : 'Từ chối xóa'">
                      <icon icon="fa-solid fa-xmark" />
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="items.length === 0">
                <td colspan="6" class="empty-state-row">
                  <icon icon="fa-solid fa-inbox" class="empty-icon" />
                  <span>{{ emptyStateText }}</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════ -->
    <!-- VIEW 2: APPROVAL & PROPOSAL HISTORY                     -->
    <!-- ═══════════════════════════════════════════════════════ -->
    <div v-else-if="mainView === 'history'" class="dashboard-panel">
      <!-- Panel Header & Entity Switcher -->
      <div class="panel-header">
        <div class="panel-title-group">
          <h3 class="panel-title">{{ isAdmin ? 'Lịch sử đã xét duyệt' : 'Lịch sử đề xuất của tôi' }}</h3>
          <span class="panel-subtitle">
            Tổng số: {{ filteredHistoryItems.length }} bản ghi
          </span>
        </div>
        <div class="panel-actions-wrapper">
          <div class="segmented-control">
            <button class="segment" :class="{ 'active': activeTab === 'book' }" @click="activeTab = 'book'">Sách</button>
            <button class="segment" :class="{ 'active': activeTab === 'author' }" @click="activeTab = 'author'">Tác giả</button>
            <button class="segment" :class="{ 'active': activeTab === 'translator' }" @click="activeTab = 'translator'">Dịch giả</button>
            <button class="segment" :class="{ 'active': activeTab === 'category' }" @click="activeTab = 'category'">Thể loại</button>
          </div>
        </div>
      </div>

      <!-- Filter Controls & Search Bar -->
      <div class="history-filter-bar">
        <div class="status-filter-group">
          <span class="filter-label">Trạng thái:</span>
          <button
            class="status-pill"
            :class="{ 'active': historyStatusFilter === 'ALL' }"
            @click="changeHistoryStatusFilter('ALL')"
          >
            Tất cả
          </button>
          <button
            class="status-pill status-pill-approved"
            :class="{ 'active': historyStatusFilter === 'APPROVED' }"
            @click="changeHistoryStatusFilter('APPROVED')"
          >
            <icon icon="fa-solid fa-circle-check" class="me-1" />
            {{ isAdmin ? 'Đã phê duyệt' : 'Đã được duyệt' }}
          </button>
          <button
            class="status-pill status-pill-rejected"
            :class="{ 'active': historyStatusFilter === 'REJECTED' }"
            @click="changeHistoryStatusFilter('REJECTED')"
          >
            <icon icon="fa-solid fa-circle-xmark" class="me-1" />
            {{ isAdmin ? 'Đã từ chối' : 'Bị từ chối' }}
          </button>
        </div>

        <div class="history-search-box">
          <icon icon="fa-solid fa-magnifying-glass" class="search-icon" />
          <input
            type="text"
            v-model="historySearchQuery"
            class="search-input"
            placeholder="Tìm theo tên, lý do, người gửi..."
          />
          <button v-if="historySearchQuery" @click="historySearchQuery = ''" class="btn-clear-search">
            <icon icon="fa-solid fa-xmark" />
          </button>
        </div>
      </div>

      <!-- History Table -->
      <div class="table-responsive-wrapper">
        <table class="custom-admin-table">
          <thead>
            <tr>
              <th class="col-stt text-center">STT</th>
              <th>Tên {{ entityLabel }}</th>
              <th class="text-center" style="width: 120px;">Loại yêu cầu</th>
              <th>Lý do & Người gửi</th>
              <th class="text-center" style="width: 140px;">Kết quả</th>
              <th>Lý do xét duyệt</th>
              <th style="width: 180px;">Người duyệt & Thời gian</th>
              <th class="text-center col-actions">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            <!-- Loading -->
            <template v-if="isHistoryTableLoading">
              <tr v-for="n in 3" :key="'skeleton-h-' + n" class="skeleton-row">
                <td class="text-center"><span class="skeleton-bar skeleton-text-stt"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td><span class="skeleton-bar skeleton-text-name"></span></td>
                <td class="text-center">
                  <div class="skeleton-actions">
                    <span class="skeleton-btn"></span>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Data Rows -->
            <template v-else>
              <tr v-for="(item, index) in filteredHistoryItems" :key="item.id + '-' + item.version + '-' + index" class="table-row-hover">
                <td class="col-stt text-center text-muted fw-semibold">{{ historyPage * 10 + index + 1 }}</td>
                <td>
                  <div class="item-name-cell">
                    <div v-if="activeTab === 'book'" class="item-cover-wrapper">
                      <img
                        v-if="item.cover"
                        :src="item.cover.startsWith('data:') || item.cover.startsWith('http') ? item.cover : 'data:image/png;base64,' + item.cover"
                        class="item-thumb-mini"
                        alt="Bìa sách"
                        @error="handleImageError($event)"
                      />
                      <div class="item-thumb-mini item-thumb-placeholder" :style="{ display: item.cover ? 'none' : 'flex' }">
                        <icon icon="fa-solid fa-book" />
                      </div>
                    </div>
                    <a v-if="activeTab === 'book'" href="#" @click.prevent="openViewModal(item)" class="row-name row-name-link" title="Click để xem chi tiết sách">
                      {{ item.name }}
                    </a>
                    <span v-else class="row-name">{{ item.name }}</span>
                  </div>
                </td>
                <td class="text-center">
                  <span class="badge-tag" :class="getActionBadgeClass(item.actionType)">
                    {{ item.actionLabel || (item.actionType === 'RESTORE' ? 'Khôi phục' : 'Xóa bỏ') }}
                  </span>
                </td>
                <td>
                  <div class="cell-stacked-info">
                    <span class="info-primary-text">{{ item.requestReason || 'Yêu cầu xử lý' }}</span>
                    <span class="info-sub-text">
                      <icon icon="fa-solid fa-user" class="me-1 fs-7-5" />
                      {{ item.requestByName || 'Nhân viên' }}
                      <span v-if="item.requestTime" class="text-muted ms-1">({{ formatVersionTime(item.requestTime) }})</span>
                    </span>
                  </div>
                </td>
                <td class="text-center">
                  <span class="badge-approval" :class="getApprovalBadgeClass(item.approvalStatus)">
                    <icon :icon="getApprovalBadgeIcon(item.approvalStatus)" class="me-1" />
                    {{ item.approvalStatusLabel }}
                  </span>
                </td>
                <td>
                  <div class="cell-stacked-info">
                    <span v-if="item.processReason" class="info-primary-text text-dark">
                      {{ item.processReason }}
                    </span>
                    <span v-else-if="item.approvalStatus === 'APPROVED'" class="text-muted fs-8-5 font-italic">
                      Đã được duyệt
                    </span>
                    <span v-else-if="item.approvalStatus === 'PENDING'" class="text-warning fs-8-5 font-italic">
                      Đang chờ xem xét
                    </span>
                    <span v-else class="text-muted fs-8-5 font-italic">
                      Không có ghi chú
                    </span>
                  </div>
                </td>
                <td>
                  <div v-if="item.processByName || item.processTime" class="cell-stacked-info">
                    <span class="info-primary-text">
                      <icon icon="fa-solid fa-user-shield" class="me-1 text-primary" />
                      {{ item.processByName || 'Quản trị viên' }}
                    </span>
                    <span v-if="item.processTime" class="info-sub-text text-muted">
                      {{ formatVersionTime(item.processTime) }}
                    </span>
                  </div>
                  <span v-else class="text-muted fs-8-5 font-italic">Chưa xử lý</span>
                </td>
                <td class="text-center">
                  <div class="action-btn-group">
                    <button @click="openHistoryModal(item)" class="btn-action-icon btn-action-history" data-tooltip="Xem lịch sử đầy đủ">
                      <icon icon="fa-solid fa-clock-rotate-left" />
                    </button>
                    <button v-if="activeTab === 'book'" @click="openViewModal(item)" class="btn-action-icon btn-action-view" data-tooltip="Xem chi tiết sách">
                      <icon icon="fa-solid fa-eye" />
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="filteredHistoryItems.length === 0">
                <td colspan="8" class="empty-state-row">
                  <icon icon="fa-solid fa-clock-rotate-left" class="empty-icon" />
                  <span>{{ emptyStateText }}</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Pagination for History -->
      <div v-if="historyTotalPages > 1" class="pagination-wrapper">
        <div class="pagination-info">
          Hiển thị <strong>{{ historyPage * 10 + 1 }}</strong> -
          <strong>{{ Math.min((historyPage + 1) * 10, historyTotalElements) }}</strong>
          trong số <strong>{{ historyTotalElements }}</strong> bản ghi
        </div>
        <div class="pagination-controls">
          <button
            @click="fetchHistoryData(historyPage - 1)"
            :disabled="historyPage === 0"
            class="btn-pagination"
            title="Trang trước"
          >
            <icon icon="fa-solid fa-chevron-left" />
          </button>
          <button
            v-for="p in historyTotalPages"
            :key="p"
            @click="fetchHistoryData(p - 1)"
            class="btn-pagination"
            :class="{ active: historyPage === p - 1 }"
          >
            {{ p }}
          </button>
          <button
            @click="fetchHistoryData(historyPage + 1)"
            :disabled="historyPage === historyTotalPages - 1"
            class="btn-pagination"
            title="Trang sau"
          >
            <icon icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- ── Approve Confirmation Modal ──────────────────────── -->
    <teleport to="body">
      <div v-if="approveModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom" :class="{ 'modal-header-restore': isRestoreItem(selectedItem) }">
            <h3 class="modal-title-custom" :class="{ 'modal-title-restore': isRestoreItem(selectedItem) }">
              <icon
                :icon="isRestoreItem(selectedItem) ? 'fa-solid fa-trash-can-arrow-up' : 'fa-solid fa-check-circle'"
                :class="isRestoreItem(selectedItem) ? 'text-success' : 'text-success'"
              />
              {{ isRestoreItem(selectedItem) ? 'Xác nhận phê duyệt khôi phục' : 'Xác nhận phê duyệt xóa' }}
            </h3>
            <button @click="closeModals" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom">
            <p class="delete-confirm-text">
              Bạn có chắc chắn muốn phê duyệt {{ isRestoreItem(selectedItem) ? 'khôi phục' : 'yêu cầu xóa' }}
              <strong>{{ selectedItem?.name }}</strong>?
            </p>
            <p v-if="isRestoreItem(selectedItem)" class="restore-info-text mb-3">
              <icon icon="fa-solid fa-circle-info" />
              Thao tác này sẽ khôi phục dữ liệu và chuyển về trạng thái phát hành (sẵn sàng sử dụng).
            </p>
            <p v-else class="delete-warn-text mb-3">
              <icon icon="fa-solid fa-circle-info" />
              Thao tác này sẽ gỡ bỏ dữ liệu khỏi hệ thống User và chuyển sang trạng thái đã xóa.
            </p>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeModals" class="btn-modal-cancel" :disabled="isProcessing">Hủy bỏ</button>
            <button
              @click="submitApprove"
              :class="isRestoreItem(selectedItem) ? 'btn-modal-restore' : 'btn-modal-confirm'"
              :disabled="isProcessing"
            >
              <icon v-if="isProcessing" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              <icon v-else-if="isRestoreItem(selectedItem)" icon="fa-solid fa-trash-can-arrow-up" class="me-1" />
              <icon v-else icon="fa-solid fa-check" class="me-1" />
              {{ isRestoreItem(selectedItem) ? 'Xác nhận Khôi phục' : 'Xác nhận Xóa' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Reason Input Modal (Reject) ──────────────────────────────── -->
    <teleport to="body">
      <div v-if="rejectModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom modal-header-danger">
            <h3 class="modal-title-custom modal-title-danger">
              <icon icon="fa-solid fa-triangle-exclamation" />
              {{ isRestoreItem(selectedItem) ? 'Từ chối yêu cầu khôi phục' : 'Từ chối yêu cầu xóa' }}
            </h3>
            <button @click="closeModals" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <p class="text-muted-dark mb-3">
              <template v-if="isRestoreItem(selectedItem)">
                Vui lòng cung cấp lý do từ chối yêu cầu khôi phục của <strong>{{ selectedItem?.name }}</strong>. Dữ liệu sẽ vẫn giữ ở trạng thái đã xóa.
              </template>
              <template v-else>
                Vui lòng cung cấp lý do từ chối yêu cầu xóa của <strong>{{ selectedItem?.name }}</strong>. Dữ liệu sẽ được giữ lại ở trạng thái đang phát hành.
              </template>
            </p>
            <div class="form-field">
              <label class="form-field-label">Lý do từ chối <span class="required-mark">*</span></label>
              <textarea
                v-model="rejectReason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.rejectReason }"
                :placeholder="isRestoreItem(selectedItem) ? 'Nhập tối thiểu 10 ký tự. Ví dụ: Dữ liệu chưa đủ điều kiện để khôi phục...' : 'Nhập tối thiểu 10 ký tự. Ví dụ: Dữ liệu này vẫn còn đang sử dụng, không được xóa...'"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.rejectReason" class="field-error-msg m-0">{{ errors.rejectReason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ rejectReason ? rejectReason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeModals" class="btn-modal-cancel" :disabled="isProcessing">Hủy bỏ</button>
            <button @click="submitReject" class="btn-modal-danger" :disabled="isProcessing">
              <icon v-if="isProcessing" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              {{ isRestoreItem(selectedItem) ? 'Xác nhận Từ chối khôi phục' : 'Xác nhận Từ chối xóa' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── View Modal (Book Detail) ────────────────────────── -->
    <teleport to="body">
      <div v-if="viewModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-view">
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-book-open" />
              Chi tiết cuốn sách
            </h3>
            <button @click="closeViewModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <div class="view-book-grid">

              <!-- Left: Cover Image -->
              <div class="view-book-left">
                <div class="cover-display-box" @click="!imageLoadError && selectedBook?.cover && openImagePreview()" :class="{ 'clickable-image': selectedBook?.cover && !imageLoadError }">
                  <img v-if="selectedBook?.cover && !imageLoadError" :src="selectedBook.cover.startsWith('data:') || selectedBook.cover.startsWith('http') ? selectedBook.cover : 'data:image/png;base64,' + selectedBook.cover" class="cover-display-img" :alt="selectedBook?.name" @error="imageLoadError = true" @load="imageLoadError = false" />
                  <div v-else class="cover-display-placeholder">
                    <icon icon="fa-solid fa-book-open" />
                  </div>
                </div>
              </div>

              <!-- Right: Book Information -->
              <div class="view-book-right">
                <!-- Book Name -->
                <div class="view-field mb-4">
                  <span class="view-field-label">Tên sách</span>
                  <p class="view-field-value">{{ selectedBook?.name }}</p>
                </div>

                <!-- Rating & Status in a clean 2-col row -->
                <div class="view-field-row mb-4">
                  <div class="view-field view-field-half">
                    <span class="view-field-label">Đánh giá</span>
                    <p class="view-field-value">
                      <icon icon="fa-solid fa-star" class="rating-star" />
                      <span>{{ selectedBook?.evaluate ? selectedBook.evaluate.toFixed(1) : '0.0' }}/5.0</span>
                    </p>
                  </div>
                  <div class="view-field view-field-half">
                    <span class="view-field-label">Trạng thái</span>
                    <p class="mb-0">
                      <span
                        class="status-badge-custom"
                        :class="{
                          'status-badge-pub': selectedBook?.status === 0 || selectedBook?.status === undefined || selectedBook?.status === null,
                          'status-badge-pending-del': selectedBook?.status === 1,
                          'status-badge-deleted': selectedBook?.status === 2
                        }"
                      >
                        {{ (selectedBook?.status === 1) ? 'Chờ xóa' : (selectedBook?.status === 2 ? 'Đã xóa' : 'Phát hành') }}
                      </span>
                    </p>
                  </div>
                </div>

                <!-- Shopee Link -->
                <div v-if="selectedBook?.link_shopee || selectedBook?.linkShopee" class="view-field mb-4">
                  <span class="view-field-label">Link Shopee</span>
                  <p class="view-field-value">
                    <a :href="selectedBook.link_shopee || selectedBook.linkShopee" target="_blank" rel="noopener noreferrer" class="shopee-link-full">
                      <icon icon="fa-solid fa-cart-shopping" />
                      Xem trên Shopee
                    </a>
                  </p>
                </div>

                <!-- Request Reason -->
                <div v-if="selectedBook?.reason || selectedBook?.requestReason" class="view-field mb-4">
                  <span class="view-field-label">Lý do yêu cầu</span>
                  <p class="view-field-value text-muted-dark">{{ selectedBook.reason || selectedBook.requestReason }}</p>
                </div>

                <!-- Process Reason -->
                <div v-if="selectedBook?.processReason" class="view-field mb-4">
                  <span class="view-field-label">Lý do xét duyệt</span>
                  <p class="view-field-value text-dark fw-semibold">{{ selectedBook.processReason }}</p>
                </div>
              </div>
            </div>

            <!-- Content Section -->
            <div class="view-content-section mt-4" v-if="selectedBook?.content">
              <span class="view-field-label">Tóm tắt nội dung</span>
              <p class="view-content-text">{{ selectedBook?.content }}</p>
            </div>

            <!-- Relations Section -->
            <div class="view-relations-section mt-4">
              <h4 class="view-relations-title">Mối quan hệ</h4>

              <!-- Authors -->
              <div v-if="selectedBook?.authors && selectedBook.authors.length" class="view-relation-block">
                <span class="view-relation-label">
                  <icon icon="fa-solid fa-user-pen" class="me-2" />
                  Tác giả
                </span>
                <div class="view-badge-group">
                  <span
                    v-for="a in selectedBook.authors"
                    :key="a.id"
                    class="badge-author view-badge"
                    :class="{ 'badge-deleted': a.status !== 0 && a.status != null }"
                    :data-tooltip="a.status !== 0 && a.status != null ? 'Tác giả đã bị xóa' : undefined"
                  >
                    {{ a.name }}<template v-if="a.yob || a.yod"> ({{ a.yob || '?' }} - {{ a.yod || '?' }})</template>
                  </span>
                </div>
              </div>

              <!-- Translators -->
              <div v-if="selectedBook?.translators && selectedBook.translators.length" class="view-relation-block">
                <span class="view-relation-label">
                  <icon icon="fa-solid fa-language" class="me-2" />
                  Dịch giả
                </span>
                <div class="view-badge-group">
                  <span
                    v-for="t in selectedBook.translators"
                    :key="t.id"
                    class="badge-translator view-badge"
                    :class="{ 'badge-deleted': t.status !== 0 && t.status != null }"
                    :data-tooltip="t.status !== 0 && t.status != null ? 'Dịch giả đã bị xóa' : undefined"
                  >
                    {{ t.name }}
                  </span>
                </div>
              </div>

              <!-- Categories -->
              <div v-if="selectedBook?.categories && selectedBook.categories.length" class="view-relation-block">
                <span class="view-relation-label">
                  <icon icon="fa-solid fa-tags" class="me-2" />
                  Thể loại
                </span>
                <div class="view-badge-group">
                  <span
                    v-for="c in selectedBook.categories"
                    :key="c.id"
                    class="badge-category view-badge"
                    :class="{ 'badge-deleted': c.status !== 0 && c.status != null }"
                    :data-tooltip="c.status !== 0 && c.status != null ? 'Thể loại đã bị xóa' : undefined"
                  >
                    {{ c.name }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeViewModal" class="btn-modal-cancel">Đóng lại</button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Image Preview Modal ─────────────────────────────── -->
    <teleport to="body">
      <div v-if="imagePreviewVisible" class="modal-backdrop-custom modal-backdrop-image" @click="closeImagePreview">
        <div class="modal-image-container" @click.stop>
          <button @click="closeImagePreview" class="btn-image-close" aria-label="Đóng">
            <icon icon="fa-solid fa-xmark" />
          </button>
          <img 
            v-if="selectedBook?.cover" 
            :src="selectedBook.cover.startsWith('data:') || selectedBook.cover.startsWith('http') ? selectedBook.cover : 'data:image/png;base64,' + selectedBook.cover" 
            class="image-preview-full" 
            :alt="selectedBook?.name" 
          />
        </div>
      </div>
    </teleport>

    <!-- ── History Modal ──────────────────────────────────── -->
    <teleport to="body">
      <div v-if="historyModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-md">
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-clock-rotate-left" />
              Lịch sử thay đổi: {{ selectedItemForHistory?.name }}
            </h3>
            <button @click="closeHistoryModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom modal-body-history">
            <div v-if="isHistoryLoading" class="text-center py-4">
              <span class="text-muted"><icon icon="fa-solid fa-spinner" class="fa-spin" /> Đang tải dữ liệu...</span>
            </div>
            <div v-else class="history-timeline">
              <div v-for="entry in historyList" :key="entry.id" class="timeline-item">
                <div class="timeline-badge" :class="getHistoryBadgeClass(entry.type || entry.action)">
                  <icon :icon="getHistoryIcon(entry.type || entry.action)" />
                </div>
                <div class="timeline-content-card">
                  <div class="timeline-header">
                    <span class="timeline-action">{{ getHistoryActionLabel(entry.type || entry.action) }}</span>
                    <span class="timeline-time">{{ formatVersionTime(entry.updateTime || entry.update_time || entry.createTime) }}</span>
                  </div>
                  <div class="timeline-actor">
                    <icon icon="fa-solid fa-user" />
                    <span>Thực hiện bởi: <strong>{{ entry.updateByName || entry.createByName || 'Quản trị viên' }}</strong></span>
                  </div>
                  <div class="timeline-details-row">
                    <p v-if="entry.type === 1 || entry.action === 'Khởi tạo'" class="timeline-details">
                      Khởi tạo {{ entityLabel.toLowerCase() }} mới.
                    </p>
                    <div v-else-if="entry.type === 2 || entry.action === 'Cập nhật thông tin'" class="timeline-details">
                      <span v-if="entry.reason">Lý do thay đổi: <strong>{{ entry.reason }}</strong></span>
                      <span v-else>Cập nhật thông tin {{ entityLabel.toLowerCase() }}.</span>
                    </div>
                    <p v-else-if="entry.type === 3 || entry.action === 'Yêu cầu xóa' || entry.action === 'Xóa bỏ'" class="timeline-details">
                      Yêu cầu xóa {{ entityLabel.toLowerCase() }}.
                      <template v-if="entry.reason">
                        <br />
                        Lý do: <strong>{{ entry.reason }}</strong>
                      </template>
                    </p>
                    <p v-else class="timeline-details">
                      <span v-if="entry.details">{{ entry.details }}</span>
                      <span v-else-if="entry.reason">Lý do: <strong>{{ entry.reason }}</strong></span>
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="historyList.length === 0" class="history-empty">
                <icon icon="fa-solid fa-clock" />
                <span>Chưa ghi nhận lịch sử thay đổi nào cho {{ entityLabel.toLowerCase() }} này.</span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeHistoryModal" class="btn-modal-cancel">Đóng lại</button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script src="./approvals.js"></script>
<style scoped src="./approvals.css"></style>
